import Phaser from "phaser";
import i18next from "i18next";
import { COLORS, hexToPhaser, darkenColor } from "../../../ui/colors";
import { ModernButton } from "../../../ui/modern-button";
import { BaseWindow } from "../../../ui/windows/base-window";
import { NotificationHandler } from "../../../ui/notification-handler";

export class NameInputWindow extends BaseWindow {
  private nameInputContainer: Phaser.GameObjects.Container | null = null;
  private loadingSpinnerContainer: Phaser.GameObjects.Container | null = null;
  private htmlInputElement: HTMLInputElement | null = null;
  private notificationHandler: NotificationHandler;
  private onClose: (() => void) | null = null;
  private uiLayer: Phaser.GameObjects.Container;

  constructor(
    scene: Phaser.Scene,
    uiLayer: Phaser.GameObjects.Container,
  ) {
    super(scene);
    this.uiLayer = uiLayer;
    this.notificationHandler = new NotificationHandler(scene);
  }

  show(currentName: string, onClose?: () => void) {
    this.onClose = onClose || null;
    this.showNameInputWindow(currentName);
  }

  hide() {
    this.hideNameInputWindow();
    super.hide();
  }

  private showNameInputWindow(currentName: string) {
    this.hideNameInputWindow();

    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    // Full screen semi-transparent background
    const bgOverlay = this.scene.add.rectangle(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
      hexToPhaser(COLORS.BLACK),
      0.8,
    );
    bgOverlay.setOrigin(0.5);
    bgOverlay.setScrollFactor(0);

    // Center panel
    const panelWidth = Math.min(400, gameWidth - 32);
    const panelHeight = 250;
    const panelX = gameWidth / 2;
    const panelY = gameHeight / 2;
    const cornerRadius = 12;

    const panelBg = this.scene.add.graphics();
    // Background
    panelBg.fillStyle(hexToPhaser(COLORS.BG), 0.92);
    panelBg.fillRoundedRect(
      panelX - panelWidth / 2,
      panelY - panelHeight / 2,
      panelWidth,
      panelHeight,
      cornerRadius,
    );
    // Outer border (black)
    const outerBorderWidth = 4;
    panelBg.lineStyle(outerBorderWidth, hexToPhaser(COLORS.BLACK), 0.8);
    panelBg.strokeRoundedRect(
      panelX - panelWidth / 2,
      panelY - panelHeight / 2,
      panelWidth,
      panelHeight,
      cornerRadius,
    );
    // Inner border (darker BG color)
    const innerBorderWidth = 4;
    const inset = (outerBorderWidth + innerBorderWidth) / 2;
    const darkerBG = darkenColor(hexToPhaser(COLORS.BG), 0.3);
    panelBg.lineStyle(innerBorderWidth, darkerBG, 0.9);
    panelBg.strokeRoundedRect(
      panelX - panelWidth / 2 + inset,
      panelY - panelHeight / 2 + inset,
      panelWidth - inset * 2,
      panelHeight - inset * 2,
      Math.max(cornerRadius - inset, 4),
    );
    panelBg.setScrollFactor(0);

    // Title
    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 24, "Edit Player Name", {
        fontSize: "24px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    title.setScrollFactor(0);

    // Calculate input field dimensions (full width with padding)
    const inputPadding = 24;
    const inputWidth = panelWidth - inputPadding * 2;
    const inputHeight = 56;
    const inputX = panelX;
    const inputY = panelY - 10;

    // Create HTML input element for native keyboard support
    this.htmlInputElement = document.createElement("input");
    this.htmlInputElement.type = "text";
    this.htmlInputElement.value = currentName;
    this.htmlInputElement.placeholder = "Enter your name";
    this.htmlInputElement.maxLength = 20;
    this.htmlInputElement.style.position = "absolute";

    // Get canvas position and calculate absolute position
    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();

    // Calculate scale factor between game coordinates and screen coordinates
    const scaleX = canvasRect.width / gameWidth;
    const scaleY = canvasRect.height / gameHeight;

    // Convert game coordinates to screen coordinates
    const screenX = canvasRect.left + inputX * scaleX;
    const screenY = canvasRect.top + inputY * scaleY;
    const screenWidth = inputWidth * scaleX;
    const screenHeight = inputHeight * scaleY;

    this.htmlInputElement.style.left = `${screenX - screenWidth / 2}px`;
    this.htmlInputElement.style.top = `${screenY - screenHeight / 2}px`;
    this.htmlInputElement.style.width = `${screenWidth}px`;
    this.htmlInputElement.style.height = `${screenHeight}px`;
    this.htmlInputElement.style.fontSize = `${22 * Math.min(scaleX, scaleY)}px`;
    this.htmlInputElement.style.fontFamily = "Anton, sans-serif";
    this.htmlInputElement.style.backgroundColor = `#${hexToPhaser(COLORS.GRAY)
      .toString(16)
      .padStart(6, "0")}`;
    this.htmlInputElement.style.color = COLORS.WHITE;
    this.htmlInputElement.style.border = `3px solid ${COLORS.WHITE}`;
    this.htmlInputElement.style.padding = "12px";
    this.htmlInputElement.style.textAlign = "center";
    this.htmlInputElement.style.outline = "none";
    this.htmlInputElement.style.zIndex = "100002";
    this.htmlInputElement.style.boxSizing = "border-box";
    document.body.appendChild(this.htmlInputElement);

    // Buttons on same row (create buttons before keyboard setup)
    const btnGap = 16;
    const btnY = panelY + panelHeight / 2 - 40;

    // Cancel button (left side)
    const cancelButton = new ModernButton({
      scene: this.scene,
      x: 0, // Will be positioned after creation
      y: btnY,
      text: i18next.t("ui:buttons.cancel") || "CANCEL",
      style: "danger",
      onClick: () => {
        this.hideNameInputWindow();
      },
    });
    cancelButton.setScrollFactor(0);

    // Save button (right side) - initially enabled
    const saveButton = new ModernButton({
      scene: this.scene,
      x: 0, // Will be positioned after creation
      y: btnY,
      text: i18next.t("ui:buttons.save") || "SAVE",
      style: "success",
      onClick: () => {
        const name = this.htmlInputElement?.value.trim() || "";
        if (name && name.length > 0) {
          this.hideNameInputWindow();
          this.showLoadingSpinner();
          this.sendNameToServer(name);
        }
      },
    });
    saveButton.setScrollFactor(0);

    // Position buttons
    const totalWidth =
      (cancelButton as any).buttonWidth +
      btnGap +
      (saveButton as any).buttonWidth;
    const cancelBtnX =
      panelX - totalWidth / 2 + (cancelButton as any).buttonWidth / 2;
    const saveBtnX =
      panelX + totalWidth / 2 - (saveButton as any).buttonWidth / 2;
    cancelButton.setX(cancelBtnX);
    saveButton.setX(saveBtnX);

    // Validation function
    const updateNameValidation = () => {
      const name = this.htmlInputElement?.value || "";
      const isValid = name.trim().length > 0;

      // Update save button appearance
      saveButton.setDisabled(!isValid);
    };

    // Listen to input changes
    this.htmlInputElement.addEventListener("input", () => {
      updateNameValidation();
    });

    // Handle Enter key to submit
    this.htmlInputElement.addEventListener(
      "keydown",
      (event: KeyboardEvent) => {
        if (event.key === "Enter") {
          const name = this.htmlInputElement?.value.trim() || "";
          if (name && name.length > 0) {
            this.hideNameInputWindow();
            this.showLoadingSpinner();
            this.sendNameToServer(name);
          }
        }
      },
    );

    // Auto-focus the input
    setTimeout(() => {
      if (this.htmlInputElement) {
        this.htmlInputElement.focus();
      }
    }, 100);

    // Initial validation
    updateNameValidation();

    // Create container
    const containerElements: Phaser.GameObjects.GameObject[] = [
      bgOverlay,
      panelBg,
      title,
      cancelButton,
      saveButton,
    ];

    this.nameInputContainer = this.scene.add.container(0, 0, containerElements);
    this.nameInputContainer.setDepth(100001);
    this.nameInputContainer.setVisible(true);

    // Add name input container to UI layer
    this.uiLayer.add(this.nameInputContainer);

    // Initial validation
    updateNameValidation();
  }

  private hideNameInputWindow() {
    // Remove HTML input element
    if (this.htmlInputElement) {
      this.htmlInputElement.remove();
      this.htmlInputElement = null;
    }

    if (this.nameInputContainer) {
      this.nameInputContainer.destroy();
      this.nameInputContainer = null;
    }

    // Call the onClose callback if provided
    if (this.onClose) {
      this.onClose();
    }
  }

  private sendNameToServer(name: string) {
    const accountId = (window as any).accountId;

    if (!accountId) {
      console.error("No account ID available");
      this.notificationHandler.show(
        "✗ Error: Account ID not found. Please try reloading.",
        "error",
        5000,
      );
      return;
    }

    const socket = (window as any).socket;
    if (!socket) {
      console.error("No socket connection");
      this.notificationHandler.show(
        "✗ Error: Not connected to server.",
        "error",
        5000,
      );
      return;
    }

    socket.emit(
      "update-name",
      { accountId, name },
      (response: { success: boolean; message?: string }) => {
        this.hideLoadingSpinner();

        if (response && response.success) {
          this.notificationHandler.show(
            "✓ Name updated successfully!",
            "success",
          );

          // Update the player name in memory
          const player = (window as any).player;
          if (player && player.data) {
            player.data.name = name;
          }
        } else {
          console.error("Failed to update name:", response.message);
          this.notificationHandler.show(
            "✗ Failed to update name. Please try again later.",
            "error",
            5000,
          );
        }
      },
    );
  }

  private showLoadingSpinner(): void {
    this.hideLoadingSpinner();

    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    // Full screen semi-transparent background
    const bgOverlay = this.scene.add.rectangle(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
      hexToPhaser(COLORS.BLACK),
      0.8,
    );
    bgOverlay.setOrigin(0.5);
    bgOverlay.setScrollFactor(0);

    // Load and display the spinning logo
    const logoSize = 128;
    const logo = this.scene.add.image(
      gameWidth / 2,
      gameHeight / 2,
      "logo-portal",
    );
    logo.setDisplaySize(logoSize, logoSize);
    logo.setScrollFactor(0);

    // Add rotation tween for spinning effect
    this.scene.tweens.add({
      targets: logo,
      angle: 360,
      duration: 2000,
      repeat: -1,
      ease: "Linear",
    });

    // Create container
    this.loadingSpinnerContainer = this.scene.add.container(0, 0, [
      bgOverlay,
      logo,
    ]);
    this.loadingSpinnerContainer.setDepth(100002);
    this.loadingSpinnerContainer.setVisible(true);

    // Add to UI layer
    this.uiLayer.add(this.loadingSpinnerContainer);
  }

  private hideLoadingSpinner(): void {
    if (this.loadingSpinnerContainer) {
      this.loadingSpinnerContainer.destroy();
      this.loadingSpinnerContainer = null;
    }
  }
}
